/**
 * Amplifier profiles -- group 06 (cost-no-object linear amplification).
 *
 * The interesting axis in this group is feedback, not power. It runs from a
 * zero-feedback 845 single-ended triode with a damping factor under three, where
 * the loudspeaker impedance curve prints itself directly onto the frequency
 * response, through zero-feedback hybrid and Class A designs, to wide-bandwidth
 * high-loop-gain solid state where output impedance is a few milliohms and the
 * load is electrically invisible. The kit selects a different nonlinear model per
 * topology, so the difference between these entries is structural rather than a
 * single distortion number.
 *
 * Most of these manufacturers publish little or no measured data. Where that is
 * true the entry is ESTIMATED and provenanceNote says which figures are
 * published and which are inferred from topology.
 */

import type { AmpProfile } from '../../types/hardware';
import { HARMONIC_SHAPES, makeAmp, usd } from '../kit';

export const AMPS_06: AmpProfile[] = [
  makeAmp({
    mfr: 'Riviera Labs',
    model: 'AIC-10',
    price: usd(
      20000,
      'APPROXIMATE',
      2023,
      'Approximately USD 20,000 in the United States; pricing varies by market and finish. Exact figure not verified.',
    ),
    year: 2015,
    country: 'Italy',
    topology: 'hybrid',
    form: 'integrated',
    devices: 'Valve voltage-gain stage direct-coupled to a zero-global-feedback Class A bipolar output',
    power: { watts8: 50, watts4: 80, confidence: 'ESTIMATED' },
    gainDb: 32,
    thdPercent: 0.15,
    snrDb: 92,
    bandwidth: [4, 120000],
    outputImpedanceOhms: 0.42,
    feedbackDb: 0,
    outputTransformer: false,
    crosstalkDb: -78,
    balancedInput: true,
    harmonicShape: HARMONIC_SHAPES.set,
    nonlinear: { kind: 'triode', amount: 0.55, asymmetry: 0.6, bias: 0.65, headroomDb: 12, oversample: 8 },
    dynamics: { sagDb: 0.7, sagTimeMs: 120, recoveryMs: 300, thermalDb: 0.3, thermalTimeMs: 7000, slewRateVus: 40, headroomDb: 1.4 },
    clipping: { kind: 'asymmetric', kneeDb: 5.5, stickiness: 0.2 },
    confidence: 'ESTIMATED',
    accent: '#b04a3c',
    tags: ['zero-feedback', 'valve-input', 'class-a-output', 'italian'],
    notes:
      'Valve voltage amplification into a Class A bipolar output with no global loop closed around either stage. The consequence the model makes audible is load interaction: at roughly 0.4 ohm source impedance the damping factor is about nineteen, so the loudspeaker impedance curve appears in the frequency response at the tenth-of-a-dB to whole-dB level instead of being flattened away. Because there is no loop to linearise the valve, the residue is second-harmonic led and tracks drive close to 1 dB per dB, and overload is a wide asymmetric knee rather than a corner. It sits deliberately between the transformer-coupled triode and the high-feedback solid state entries in this group.',
    provenanceNote:
      'The topology -- valve gain stage, no global feedback, Class A solid-state output -- is from Riviera Labs published description. Riviera publishes no THD, signal-to-noise, output impedance or bandwidth figures; power, distortion, noise, bandwidth and output impedance here are ESTIMATED from the class of zero-feedback hybrid integrated amplifiers.',
  }),

  makeAmp({
    mfr: 'Engstrom',
    model: 'Lars',
    price: usd(
      185000,
      'APPROXIMATE',
      2023,
      'Approximately USD 185,000 per pair. Built to order and pricing is not consistently published; exact figure not verified.',
    ),
    year: 2015,
    country: 'Sweden',
    topology: 'tube-set',
    form: 'monoblock',
    devices: '845 directly-heated triode output, single-ended, wound output transformer, no global feedback',
    power: { watts8: 25, watts4: 16, confidence: 'ESTIMATED' },
    gainDb: 30,
    thdPercent: 1.6,
    snrDb: 82,
    bandwidth: [17, 26000],
    outputImpedanceOhms: 3.1,
    feedbackDb: 0,
    outputTransformer: true,
    transformerLfHz: 19,
    transformerHfHz: 24000,
    crosstalkDb: -90,
    harmonicShape: HARMONIC_SHAPES.set,
    nonlinear: { kind: 'triode', amount: 0.9, asymmetry: 0.82, bias: 0.88, headroomDb: 7, oversample: 8 },
    dynamics: { sagDb: 2.4, sagTimeMs: 260, recoveryMs: 640, slewRateVus: 12, headroomDb: 0.8 },
    clipping: { kind: 'soft', kneeDb: 9.5, stickiness: 0.45 },
    confidence: 'ESTIMATED',
    accent: '#e6c37a',
    tags: ['845', 'directly-heated', 'zero-feedback', 'transformer-coupled'],
    notes:
      'Zero-feedback 845 single-ended triode into a wound output transformer, one chassis per channel. With no loop feedback the source impedance is around 3 ohms and the damping factor is under three, which means the modelled frequency response is essentially the loudspeaker impedance curve scaled by a few dB; the profile is only coherent when paired with a high-sensitivity, benign-impedance load. Distortion is nearly pure second harmonic at low level, grows about 1 dB per dB of drive, and the transformer adds core saturation below roughly 30 Hz that the model applies before any clipping occurs. Supply sag of over 2 dB on sustained peaks makes the dynamic envelope compress in a way none of the solid-state entries do.',
    provenanceNote:
      'The 845 output complement and the monoblock construction are from the Engstrom product description. Engstrom publishes essentially no electrical data for the Lars: the single-ended operating class, rated power, THD, signal-to-noise, bandwidth, output impedance and transformer corners in this entry are ESTIMATED from the class of zero-feedback directly-heated triode monoblocks and are NOT quoted specifications.',
  }),

  makeAmp({
    mfr: 'Vitus Audio',
    model: 'SIA-025 Mk II',
    price: usd(
      40000,
      'APPROXIMATE',
      2023,
      'Approximately USD 40,000. Exact figure not verified.',
    ),
    year: 2018,
    country: 'Denmark',
    topology: 'solid-state-a',
    form: 'integrated',
    devices: 'Fully balanced bipolar output biased into Class A to the rated 25 W, switchable to Class AB above',
    power: { watts8: 25, watts4: 50, confidence: 'ESTIMATED' },
    gainDb: 34,
    thdPercent: 0.01,
    snrDb: 105,
    bandwidth: [2, 300000],
    dampingFactor: 400,
    feedbackDb: 10,
    crosstalkDb: -100,
    balancedInput: true,
    harmonicShape: HARMONIC_SHAPES.classA,
    nonlinear: { kind: 'jfet-square', amount: 0.45, asymmetry: 0.45, bias: 0.6, headroomDb: 13, oversample: 4 },
    dynamics: { sagDb: 0.25, sagTimeMs: 40, recoveryMs: 110, thermalDb: 0.5, thermalTimeMs: 12000, slewRateVus: 55, headroomDb: 2.4 },
    clipping: { kind: 'asymmetric', kneeDb: 3.5, stickiness: 0.1 },
    confidence: 'ESTIMATED',
    accent: '#4f7d6a',
    tags: ['class-a', 'balanced', 'switchable-bias', 'danish'],
    notes:
      'Fully balanced Class A integrated with a supply and heatsink mass far larger than a 25 W rating implies, which is why the modelled headroom above continuous rating is over 2 dB while supply sag is nearly absent. Running the output devices in Class A removes the crossover region entirely, so there is no high-order crossover spray in the residue and the harmonic series decays fast above the third. The largest dynamic effect in this profile is thermal rather than electrical: bias drift over tens of seconds of sustained level produces about half a dB of slow gain movement, a mechanism the Class AB entries in this group do not exhibit and one that only appears on long loud passages.',
    provenanceNote:
      'The Class A rating, the balanced topology and the Class A to Class AB switching are from Vitus published material. Vitus publishes limited electrical data; THD, signal-to-noise, damping factor, bandwidth and the thermal behaviour here are ESTIMATED.',
  }),

  makeAmp({
    mfr: 'Gryphon Audio Designs',
    model: 'Antileon EVO',
    price: usd(
      45000,
      'APPROXIMATE',
      2023,
      'Approximately USD 45,000 for the stereo version. Exact figure not verified.',
    ),
    year: 2017,
    country: 'Denmark',
    topology: 'solid-state-a',
    form: 'stereo-power',
    devices: 'Pure Class A bipolar output, dual mono, DC-coupled, minimal overall feedback',
    power: { watts8: 150, watts4: 300, watts2: 600, confidence: 'ESTIMATED' },
    gainDb: 26,
    thdPercent: 0.02,
    snrDb: 103,
    bandwidth: [0.3, 350000],
    dampingFactor: 800,
    feedbackDb: 6,
    crosstalkDb: -95,
    balancedInput: true,
    harmonicShape: HARMONIC_SHAPES.classA,
    nonlinear: { kind: 'jfet-square', amount: 0.38, asymmetry: 0.3, bias: 0.5, headroomDb: 15, oversample: 4 },
    dynamics: { sagDb: 0.12, sagTimeMs: 30, recoveryMs: 90, thermalDb: 0.28, thermalTimeMs: 15000, slewRateVus: 80, headroomDb: 1.6 },
    clipping: { kind: 'asymmetric', kneeDb: 2.8, stickiness: 0.06 },
    confidence: 'ESTIMATED',
    accent: '#3f5d86',
    tags: ['class-a', 'current-doubling', 'dc-coupled', 'low-impedance-capable'],
    notes:
      'Pure Class A with the device count and supply reserve sized so output doubles from 8 to 4 to 2 ohms. In this model that single property is what separates it from the Vitus entry: into a 2 ohm impedance dip it still behaves as a voltage source, so the response through the dip stays flat, where a supply-limited design would sag and compress exactly where the loudspeaker demands most current. Damping factor near 800 leaves load interaction below a tenth of a dB, so the loudspeaker response passes through unmodified. Distortion is low, second-harmonic led and slowly rising with level, and the clipping knee is narrow because the rails are stiff.',
    provenanceNote:
      'Class A operation, dual-mono construction, DC coupling and the 8 / 4 / 2 ohm power doubling are from Gryphon published material. THD, signal-to-noise, damping factor and bandwidth are ESTIMATED; Gryphon publishes limited measured data for this model.',
  }),

  makeAmp({
    mfr: 'Boulder Amplifiers',
    model: '2160',
    price: usd(
      52000,
      'APPROXIMATE',
      2023,
      'Approximately USD 52,000. Exact figure not verified.',
    ),
    year: 2016,
    country: 'United States',
    topology: 'solid-state-ab',
    form: 'stereo-power',
    devices: 'Boulder 983 discrete gain stages, large complementary bipolar output array per channel',
    power: { watts8: 600, watts4: 1000, confidence: 'ESTIMATED' },
    gainDb: 26,
    thdPercent: 0.0008,
    snrDb: 120,
    bandwidth: [0.4, 500000],
    dampingFactor: 2000,
    feedbackDb: 34,
    crosstalkDb: -110,
    balancedInput: true,
    harmonicShape: HARMONIC_SHAPES.highFeedback,
    nonlinear: { kind: 'class-b-crossover', amount: 0.22, crossoverWidth: 0.0012, headroomDb: 14, asymmetry: 0.08, oversample: 4 },
    dynamics: { sagDb: 0.1, sagTimeMs: 25, recoveryMs: 80, thermalDb: 0.08, thermalTimeMs: 9000, slewRateVus: 100, headroomDb: 1.2 },
    clipping: { kind: 'hard', kneeDb: 0.9, stickiness: 0.05 },
    confidence: 'ESTIMATED',
    accent: '#8d8f96',
    tags: ['high-feedback', 'discrete-gain-stage', 'high-power', 'balanced-throughout'],
    notes:
      'High loop gain wrapped around discrete gain stages, with enough output devices that each one works far inside its safe operating area. What the feedback leaves behind is not simply less distortion but distortion of a different shape: low-order products are suppressed hard and the residue redistributes into higher orders with a slowly decaying tail, which is why this profile uses the high-feedback harmonic distribution rather than the Class A one. Crossover distortion exists but the modelled notch is very narrow, so it is only significant at very low signal levels. Overload is abrupt with almost no soft knee, because the loop keeps correcting right up to the point where it runs out of rail.',
    provenanceNote:
      'Rated power, the discrete gain-stage architecture and the balanced-throughout signal path are from Boulder published material. THD, signal-to-noise, damping factor and bandwidth here are recalled approximations of Boulder published figures and are tagged ESTIMATED. The harmonic distribution is a MODELLED consequence of high loop gain, not a measurement.',
  }),

  makeAmp({
    mfr: 'Constellation Audio',
    model: 'Virgo III',
    price: usd(
      29000,
      'APPROXIMATE',
      2023,
      'Approximately USD 29,000. Exact figure not verified.',
    ),
    year: 2017,
    country: 'United States',
    topology: 'solid-state-a',
    form: 'preamp',
    devices: 'Balanced Class A discrete Line Stage Gain Module derived from the Reference series',
    power: { outputVrms: 12, confidence: 'ESTIMATED' },
    gainDb: 12,
    thdPercent: 0.004,
    snrDb: 108,
    bandwidth: [1, 500000],
    outputImpedanceOhms: 100,
    feedbackDb: 8,
    crosstalkDb: -105,
    balancedInput: true,
    harmonicShape: HARMONIC_SHAPES.classA,
    nonlinear: { kind: 'jfet-square', amount: 0.25, asymmetry: 0.3, bias: 0.5, headroomDb: 18, oversample: 4 },
    dynamics: { sagDb: 0.02, sagTimeMs: 15, recoveryMs: 50, thermalDb: 0.05, thermalTimeMs: 6000, slewRateVus: 150, headroomDb: 6 },
    clipping: { kind: 'asymmetric', kneeDb: 2, stickiness: 0.05 },
    confidence: 'ESTIMATED',
    accent: '#6d5f9c',
    tags: ['line-stage', 'class-a', 'balanced', 'high-headroom'],
    notes:
      'A line stage, so this profile carries no power rating at all: what it contributes to a chain is voltage gain, an output impedance and a distortion residue applied ahead of the power amplifier rather than into the loudspeaker. The balanced Class A gain cell holds a low second-harmonic-led residue that barely changes with level, and 12 V of clean output swing means the modelled headroom above normal listening level is around 6 dB, so it never becomes the limiting element. The 100 ohm output impedance is low enough that neither cable capacitance nor a typical power amplifier input impedance reshapes the response, which is what makes it useful as the near-transparent reference against which valve line stages are compared.',
    provenanceNote:
      'The balanced Class A line-stage topology and the Line Stage Gain Module description are from Constellation published material. Constellation publishes limited electrical data for the Virgo III; gain, THD, signal-to-noise, output swing, output impedance and bandwidth are ESTIMATED.',
  }),

  makeAmp({
    mfr: 'Soulution',
    model: '701',
    price: usd(
      130000,
      'APPROXIMATE',
      2023,
      'Approximately USD 130,000 per pair. Exact figure not verified.',
    ),
    year: 2010,
    country: 'Switzerland',
    topology: 'solid-state-ab',
    form: 'monoblock',
    devices: 'Very wide open-loop bandwidth voltage amplifier with fast local feedback, large bipolar output array',
    power: { watts8: 430, watts4: 800, confidence: 'ESTIMATED' },
    gainDb: 26,
    thdPercent: 0.0006,
    snrDb: 122,
    bandwidth: [0.5, 800000],
    dampingFactor: 10000,
    feedbackDb: 40,
    crosstalkDb: -125,
    balancedInput: true,
    harmonicShape: HARMONIC_SHAPES.highFeedback,
    nonlinear: { kind: 'class-b-crossover', amount: 0.18, crossoverWidth: 0.0008, headroomDb: 15, asymmetry: 0.05, oversample: 4 },
    dynamics: { sagDb: 0.06, sagTimeMs: 20, recoveryMs: 70, thermalDb: 0.06, thermalTimeMs: 8000, slewRateVus: 200, headroomDb: 1.0 },
    clipping: { kind: 'hard', kneeDb: 0.6, stickiness: 0.04 },
    confidence: 'ESTIMATED',
    accent: '#c0c6cc',
    tags: ['wide-bandwidth', 'fast-settling', 'very-high-damping', 'swiss'],
    notes:
      'The Soulution design argument is settling time rather than steady-state distortion: an amplifier whose open-loop bandwidth extends far past the audio band can apply large amounts of feedback without the correction lagging the signal it is correcting. The model expresses that as a closed-loop corner near 800 kHz, a 200 V per microsecond slew limit and a damping factor around 10,000, meaning a source impedance of under a milliohm and a loudspeaker impedance curve that is electrically invisible. Distortion is minute but high-order in structure, and clipping is hard, so what separates this profile from the Class A entries shows up in overload behaviour and in load independence rather than anywhere in the linear range.',
    provenanceNote:
      'The wide-bandwidth fast-settling design philosophy, the monoblock construction and the rated power are from Soulution published material. THD, signal-to-noise, damping factor, bandwidth and slew rate here are recalled approximations of published figures and are tagged ESTIMATED rather than MANUFACTURER_SPEC.',
  }),

  makeAmp({
    mfr: 'CH Precision',
    model: 'A1.5',
    price: usd(
      38000,
      'APPROXIMATE',
      2023,
      'Approximately USD 38,000. Exact figure not verified.',
    ),
    year: 2019,
    country: 'Switzerland',
    topology: 'solid-state-ab',
    form: 'stereo-power',
    devices: 'Stereo, bridged or bi-amp configurable output with a user-adjustable global to local feedback ratio',
    power: { watts8: 100, watts4: 175, confidence: 'ESTIMATED' },
    gainDb: 24,
    thdPercent: 0.03,
    snrDb: 110,
    bandwidth: [1, 400000],
    dampingFactor: 350,
    feedbackDb: 12,
    crosstalkDb: -100,
    balancedInput: true,
    harmonicShape: HARMONIC_SHAPES.classAB,
    nonlinear: { kind: 'class-b-crossover', amount: 0.35, crossoverWidth: 0.003, headroomDb: 12, asymmetry: 0.12, oversample: 4 },
    dynamics: { sagDb: 0.2, sagTimeMs: 35, recoveryMs: 100, thermalDb: 0.15, thermalTimeMs: 9000, slewRateVus: 70, headroomDb: 1.5 },
    clipping: { kind: 'asymmetric', kneeDb: 2.0, stickiness: 0.08 },
    confidence: 'ESTIMATED',
    accent: '#2f6f8f',
    tags: ['adjustable-feedback', 'reconfigurable', 'class-ab', 'swiss'],
    notes:
      'The A1.5 exposes the feedback ratio as a user control, sweeping the global loop from zero to full while local feedback in each stage takes up the remainder. This profile is authored at a low global-feedback setting, which is why the modelled THD and output impedance are an order of magnitude worse than the Boulder and Soulution entries even though the circuit can match them: with the global loop backed off, the residue stays low-order and the damping factor falls to a few hundred, so the loudspeaker impedance curve becomes partly visible again. Setting it high would move this profile most of the way toward the high-loop-gain entries, which is exactly the comparison the control exists to make.',
    provenanceNote:
      'The configurable output modes and the adjustable global feedback control are from CH Precision published material. Rated power is approximate. THD, signal-to-noise, damping factor and bandwidth are ESTIMATED for the low-global-feedback setting this profile represents and are not CH Precision quoted figures, which are stated at a different setting.',
  }),
];
